// Shared writability rules for the vendor item write surfaces (HUBX-8474).
//
// `writableFields` (ADR-0011) is what the SERVER said this caller may write on this item.
// Everything here reads that list and nothing else: no role is inferred, no permission is
// guessed from the shape of the data.
//
// The list travels resolved PER ROW and is empty outside the caller's write scope, so an
// empty list disables everything — which is the correct reading of "you may write nothing".

import { FormGroup } from '@angular/forms';

/** Contract path -> the control(s) it governs. An explicit table, NOT a naming convention:
 *  six of the paths do not match their control name, and `categoryId` governs two controls
 *  because the main select is a UI-only step on the way to it (its `<select>` is the ONLY
 *  one rendered when the category has no children, so disabling the leaf alone would leave
 *  the category editable).
 *
 *  What is deliberately absent, and why:
 *  - `tags`, `attributes.*` — real write members with no control in these forms. They
 *    round-trip from the item as read.
 *  - `isArchived` — drives the Archive action (see mayArchive), not a form control. */
export const WRITABLE_CONTROLS: { [path: string]: string[] } = {
  description: ['description'],
  categoryId: ['categoryId', 'categoryMainId'],
  unitPurchasePrice: ['cost'],
  availability: ['availability'],
  minimumOrderQty: ['moq'],
  maximumOrderQty: ['mxq'],
  leadTimeDays: ['leadTimeDays'],
  exwId: ['exwId'],
  specialNotes: ['specialNotes'],
  restrictions: ['restrictions'],
  unitPrice: ['unitPrice'],
  discountQtys: ['discountQtys'],
  hubxItemCode: ['hubxItemCode'],
  // The collapsed control IS the status intent (ADR-0009 Rule 9): the server lists `status`
  // for every role that may move either axis, and decides which axis a pick moves.
  status: ['skuStatus'],
  todaysDeals: ['todaysDeals'],
  'attributes.Condition': ['condition'],
  'attributes.Packaging': ['packaging'],
  'attributes.Warranty': ['warranty']
};

/** Whether the aggregate is writable AT ALL, or only its curation row — the HUBX-owned
 *  case, where SAP owns the operational state and no UPDATE may reach dbo.VendorItems.
 *
 *  Read off `attributes.*` because that is the one entry the server documents as "always
 *  writable when the aggregate is writable at all". Any other member would be a proxy that
 *  a later role rule could silently invalidate. */
export function isAggregateWritable(writableFields: string[]): boolean {
  return writableFields.includes('attributes.*');
}

/** Whether the Archive / Restore action is offered (HUBX-8438). Server-stated, like every
 *  other permission here — not derived from the role. */
export function mayArchive(writableFields: string[]): boolean {
  return writableFields.includes('isArchived');
}

/** Whether the caller may MOVE Today's Deals. Load-bearing twice: it disables the chip, and
 *  it decides whether the member is sent at all — the PUT treats an ABSENT `todaysDeals` as
 *  "do not touch", so a caller who may not move it simply omits it rather than echoing the
 *  value read. */
export function mayCurate(writableFields: string[]): boolean {
  return writableFields.includes('todaysDeals');
}

/** Whether nothing on this item is writable for this caller (HUBX-8493). The server sends
 *  an empty list only when it would refuse every write: outside the caller's write scope,
 *  as for a VendorManager on a company B1 does not assign to it (HUBX-8492). A HUBX-owned
 *  item keeps its curation fields for an operator, and a B1 outage keeps the full list, so
 *  neither reads as this. For an EXISTING item only — on Create there is no list to consult. */
export function isReadOnly(writableFields: string[]): boolean {
  return writableFields.length === 0;
}

/** Turn `writableFields` into enabled/disabled controls (ADR-0011). For an EXISTING item
 *  only — on Create there is no per-item permission to consult and the role rules in the
 *  form factory are the whole answer.
 *
 *  The list is a CEILING, so this only ever REMOVES what the server did not grant; it never
 *  grants beyond the contract, because every path in the table is a real write member.
 *  Disabling is safe for the round trip: the request is built from getRawValue(), so a
 *  disabled control still travels as full state.
 *
 *  emitEvent:false throughout — this is not a user edit, and letting it through would clear
 *  a server error the user has not addressed yet. */
export function applyWritability(fg: FormGroup, writableFields: string[]): void {
  const writable = new Set(writableFields);

  for (const [path, controls] of Object.entries(WRITABLE_CONTROLS)) {
    for (const name of controls) {
      const control = fg.get(name);
      if (!control) {
        continue;
      }
      if (writable.has(path)) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }
  }

  // Three that are never writable on an EXISTING item, and are absent from the server's
  // list for three different reasons. Stated here rather than inferred from the absence,
  // because absence alone does not distinguish them from the two below.
  // - vendorItemCode: immutable for everyone (§4.2), so the server never lists it.
  // - status: the Create form's CONTROL, mapped to createApproved, a Create-only intent —
  //   not the `status` contract path above, which governs `skuStatus`.
  // - targetVendorId: an existing item's vendor is not retargetable (ADR-0010).
  for (const name of ['vendorItemCode', 'status', 'targetVendorId']) {
    fg.get(name)?.disable({ emitEvent: false });
  }

  // The master fields, absent for the OPPOSITE reason: not "never", but "not decidable
  // here". Their permission turns on the sharedness of the destination master, which
  // depends on an MPN the user has not typed yet, so the server refuses to guess and
  // GET /items/resolutions answers per keystroke instead. Disabling them on absence would
  // take away the two fields users most need to edit. They follow the aggregate: editable
  // whenever anything is.
  const aggregate = isAggregateWritable(writableFields);
  for (const name of ['mpn', 'manufacturerId']) {
    const control = fg.get(name);
    if (!control) {
      continue;
    }
    if (aggregate) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }
}
